import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — SavedViewsTabs (HubSpot Style Horizontal Views)
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Displays saved views horizontally as draggable, reorderable tabs.
 * Supports:
 *  - 1-click view switching
 *  - Drag-and-drop reordering (moving view like sorting)
 *  - Active tab options (save modifications, rename, delete, move left/right)
 *  - Inline (+) button to quickly save current view state
 */

// Helper to strip non-view-state keys for comparison and storage
const getCleanState = (fullState) => {
    const { activeViewId, ...rest } = fullState || {};
    return rest;
};

function deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true;
    if (obj1 == null || typeof obj1 !== 'object' || obj2 == null || typeof obj2 !== 'object') return false;
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    for (let key of keys1) {
        if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
    }
    return true;
}

export function SavedViewsTabs({ pageId, defaultTitle = 'All', viewState = {}, setFullViewState }) {
    const [savedViews, setSavedViews] = useState([]);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newViewName, setNewViewName] = useState('');
    const [editingViewId, setEditingViewId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [activeMenuViewId, setActiveMenuViewId] = useState(null);
    const menuRef = useRef(null);

    // Load saved views from localStorage
    useEffect(() => {
        if (!pageId) return;
        const stored = localStorage.getItem(`oblique_savedViews_${pageId}`);
        if (stored) {
            try { 
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    setSavedViews(parsed);
                }
            } catch (e) {}
        }
    }, [pageId]);

    // Close options menu on click outside
    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setActiveMenuViewId(null);
            }
        };
        if (activeMenuViewId) {
            document.addEventListener('mousedown', handler);
        }
        return () => document.removeEventListener('mousedown', handler);
    }, [activeMenuViewId]);

    // Construct full views list with default "All" view at index 0
    const defaultView = useMemo(() => ({
        id: 'default',
        name: defaultTitle,
        isDefault: true,
        state: { filters: [], sorts: [], viewMode: 'list' }
    }), [defaultTitle]);

    const activeViewId = viewState?.activeViewId || 'default';

    const activeView = useMemo(() => {
        if (!viewState?.activeViewId || viewState.activeViewId === 'default') return defaultView;
        return savedViews.find(v => v.id === viewState.activeViewId) || defaultView;
    }, [viewState?.activeViewId, savedViews, defaultView]);

    const hasUnsavedChanges = useMemo(() => {
        if (!activeView || activeView.isDefault || !viewState) return false;
        return !deepEqual(getCleanState(viewState), activeView.state);
    }, [viewState, activeView]);

    // Actions
    const handleSelectView = (view) => {
        if (view.isDefault) {
            setFullViewState({ filters: [], sorts: [], viewMode: 'list', activeViewId: 'default' });
        } else {
            setFullViewState({ ...view.state, activeViewId: view.id });
        }
    };

    const handleReorderViews = (newOrder) => {
        setSavedViews(newOrder);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(newOrder));
    };

    const handleMoveView = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= savedViews.length) return;
        const reordered = [...savedViews];
        const [moved] = reordered.splice(index, 1);
        reordered.splice(targetIndex, 0, moved);
        handleReorderViews(reordered);
    };

    const handleSaveNewView = () => {
        if (!newViewName.trim()) {
            setIsAddingNew(false);
            return;
        }
        const newView = {
            id: `view_${Date.now()}`,
            name: newViewName.trim(),
            state: getCleanState(viewState)
        };
        const updated = [...savedViews, newView];
        setSavedViews(updated);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(updated));
        setFullViewState(prev => ({ ...prev, activeViewId: newView.id }));
        setIsAddingNew(false);
        setNewViewName('');
    };

    const handleUpdateActiveView = (view) => {
        const updated = savedViews.map(v =>
            v.id === view.id ? { ...v, state: getCleanState(viewState) } : v
        );
        setSavedViews(updated);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(updated));
        setActiveMenuViewId(null);
    };

    const handleRenameView = (viewId) => {
        if (!editingName.trim()) {
            setEditingViewId(null);
            return;
        }
        const updated = savedViews.map(v =>
            v.id === viewId ? { ...v, name: editingName.trim() } : v
        );
        setSavedViews(updated);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(updated));
        setEditingViewId(null);
        setEditingName('');
        setActiveMenuViewId(null);
    };

    const handleDeleteView = (viewId) => {
        const updated = savedViews.filter(v => v.id !== viewId);
        setSavedViews(updated);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(updated));
        if (activeViewId === viewId) {
            handleSelectView(defaultView);
        }
        setActiveMenuViewId(null);
    };

    // Keep UI simple: Only show horizontal view tabs row if custom views exist
    if (savedViews.length === 0) {
        return null;
    }

    return (
        <div className="w-full flex items-center justify-between border-b border-[var(--border)] bg-[var(--bg-surface)] px-4 md:px-5 py-0 select-none overflow-x-auto custom-scrollbar">
            <div className="flex items-center gap-1 py-1.5 min-w-0">
                {/* Default "All" Tab */}
                <button
                    type="button"
                    onClick={() => handleSelectView(defaultView)}
                    className={`h-[28px] px-3 rounded-md text-[13px] font-medium transition-all flex items-center gap-1.5 shrink-0 cursor-pointer border-0 relative ${
                        activeViewId === 'default'
                            ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-semibold shadow-2xs'
                            : 'bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]'
                    }`}
                >
                    <ObliqueIcon 
                        name="table_chart" 
                        size={14} 
                        color={activeViewId === 'default' ? 'var(--accent)' : 'var(--text-tertiary)'} 
                    />
                    <span>{defaultView.name}</span>
                    {activeViewId === 'default' && (
                        <span className="absolute bottom-[-6px] left-2 right-2 h-[2px] bg-[var(--accent)] rounded-full" />
                    )}
                </button>

                {/* Draggable Saved Views */}
                <Reorder.Group 
                    axis="x" 
                    values={savedViews} 
                    onReorder={handleReorderViews} 
                    className="flex items-center gap-1 list-none p-0 m-0 shrink-0"
                >
                    {savedViews.map((view, idx) => {
                        const isActive = activeViewId === view.id;
                        const isEditing = editingViewId === view.id;

                        return (
                            <Reorder.Item 
                                key={view.id} 
                                value={view}
                                className="relative list-none"
                            >
                                {isEditing ? (
                                    <div className="flex items-center gap-1 h-[28px] px-2 rounded-md bg-[var(--bg-subtle)] border border-[var(--accent)]">
                                        <input
                                            autoFocus
                                            type="text"
                                            value={editingName}
                                            onChange={(e) => setEditingName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleRenameView(view.id);
                                                if (e.key === 'Escape') setEditingViewId(null);
                                            }}
                                            className="!bg-transparent text-[12px] text-[var(--text-primary)] font-medium !outline-none !border-none !p-0 w-24"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleRenameView(view.id)}
                                            className="text-[11px] font-semibold text-[var(--accent)] bg-transparent border-0 cursor-pointer"
                                        >
                                            Save
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        onClick={() => handleSelectView(view)}
                                        className={`group h-[28px] pl-2.5 pr-1.5 rounded-md text-[13px] font-medium transition-all flex items-center gap-1.5 shrink-0 cursor-pointer relative ${
                                            isActive
                                                ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)] font-semibold shadow-2xs'
                                                : 'bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-subtle)]'
                                        }`}
                                    >
                                        <ObliqueIcon 
                                            name="grid_view" 
                                            size={14} 
                                            color={isActive ? 'var(--accent)' : 'var(--text-tertiary)'} 
                                        />
                                        <span className="truncate max-w-[150px]">{view.name}</span>
                                        
                                        {isActive && (
                                            <span className="absolute bottom-[-6px] left-2 right-2 h-[2px] bg-[var(--accent)] rounded-full" />
                                        )}

                                        {/* Tab Menu Options Trigger */}
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveMenuViewId(activeMenuViewId === view.id ? null : view.id);
                                            }}
                                            className={`p-0.5 rounded hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors border-0 cursor-pointer ${
                                                isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                            }`}
                                            title="View options & reordering"
                                        >
                                            <ObliqueIcon name="more_vert" size={13} />
                                        </button>

                                        {/* Tab Menu Popover */}
                                        <AnimatePresence>
                                            {activeMenuViewId === view.id && (
                                                <motion.div
                                                    ref={menuRef}
                                                    initial={{ opacity: 0, scale: 0.95, y: 4 }}
                                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                                    exit={{ opacity: 0, scale: 0.95, y: 4 }}
                                                    transition={{ duration: 0.12 }}
                                                    className="absolute top-full left-0 mt-1.5 w-44 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-lg z-[3000] p-1 flex flex-col gap-0.5"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {isActive && hasUnsavedChanges && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleUpdateActiveView(view)}
                                                            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[var(--accent)] hover:bg-[var(--bg-subtle)] border-0 cursor-pointer text-left w-full"
                                                        >
                                                            <ObliqueIcon name="save" size={14} />
                                                            <span>Save view changes</span>
                                                        </button>
                                                    )}

                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setEditingViewId(view.id);
                                                            setEditingName(view.name);
                                                            setActiveMenuViewId(null);
                                                        }}
                                                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium text-[var(--text-primary)] hover:bg-[var(--bg-subtle)] border-0 cursor-pointer text-left w-full"
                                                    >
                                                        <ObliqueIcon name="edit" size={14} color="var(--text-tertiary)" />
                                                        <span>Rename view</span>
                                                    </button>

                                                    <div className="flex items-center justify-between px-2 py-1 border-t border-[var(--border-subtle)] text-[11px] text-[var(--text-tertiary)]">
                                                        <span>Move view:</span>
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                type="button"
                                                                disabled={idx === 0}
                                                                onClick={() => handleMoveView(idx, -1)}
                                                                className="p-1 rounded hover:bg-[var(--bg-subtle)] disabled:opacity-30 border-0 cursor-pointer text-[var(--text-secondary)]"
                                                                title="Move Left"
                                                            >
                                                                <ObliqueIcon name="arrow_back" size={12} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={idx === savedViews.length - 1}
                                                                onClick={() => handleMoveView(idx, 1)}
                                                                className="p-1 rounded hover:bg-[var(--bg-subtle)] disabled:opacity-30 border-0 cursor-pointer text-[var(--text-secondary)]"
                                                                title="Move Right"
                                                            >
                                                                <ObliqueIcon name="arrow_forward" size={12} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="h-[1px] bg-[var(--border-subtle)] my-0.5" />

                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteView(view.id)}
                                                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium text-[var(--danger)] hover:bg-[var(--danger-subtle)] border-0 cursor-pointer text-left w-full"
                                                    >
                                                        <ObliqueIcon name="delete" size={14} />
                                                        <span>Delete view</span>
                                                    </button>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}
                            </Reorder.Item>
                        );
                    })}
                </Reorder.Group>

                {/* (+) Add View Tab with Clean Popover */}
                <div className="relative shrink-0">
                    <button
                        type="button"
                        onClick={() => setIsAddingNew(!isAddingNew)}
                        className={`h-[28px] w-[28px] rounded-md transition-colors flex items-center justify-center border-0 cursor-pointer ${
                            isAddingNew 
                                ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)]' 
                                : 'hover:bg-[var(--bg-subtle)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'
                        }`}
                        title="Save current filters as a new view"
                    >
                        <ObliqueIcon name="add" size={16} />
                    </button>

                    <AnimatePresence>
                        {isAddingNew && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 4 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 4 }}
                                transition={{ duration: 0.12 }}
                                className="absolute top-full left-0 mt-1.5 w-52 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-lg z-[3000] p-2.5 flex flex-col gap-2"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <span className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">
                                    Name this view
                                </span>
                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="e.g. High Priority..."
                                    value={newViewName}
                                    onChange={(e) => setNewViewName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveNewView();
                                        if (e.key === 'Escape') setIsAddingNew(false);
                                    }}
                                    className="w-full px-2 py-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-subtle)] text-xs text-[var(--text-primary)] !outline-none focus:border-[var(--accent)]"
                                />
                                <div className="flex items-center justify-end gap-1.5 pt-0.5">
                                    <button
                                        type="button"
                                        onClick={() => setIsAddingNew(false)}
                                        className="px-2.5 py-1 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] rounded-md border-0 cursor-pointer"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveNewView}
                                        disabled={!newViewName.trim()}
                                        className="px-3 py-1 text-xs font-semibold bg-[var(--accent)] text-white disabled:opacity-50 rounded-md border-0 cursor-pointer"
                                    >
                                        Save
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {/* Quick Unsaved Changes Indicator & Sync */}
            {hasUnsavedChanges && (
                <div className="flex items-center gap-2 shrink-0 py-1 pl-3">
                    <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                        Unsaved filter changes
                    </span>
                    <button
                        type="button"
                        onClick={() => handleUpdateActiveView(activeView)}
                        className="text-[11px] font-bold text-[var(--accent)] hover:underline bg-transparent border-0 cursor-pointer"
                    >
                        Save
                    </button>
                </div>
            )}
        </div>
    );
}
export default SavedViewsTabs;
